import React, { useState, useEffect, useRef } from 'react';
import './Game.css';
import '../styles/PageLayout.css';
import { gameAPI } from '../api/game';
import { userAPI } from '../api/user';
import api from '../api/index';
import UserInfo from '../components/UserInfo';
import '../components/UserInfo.css';
import {
  clampSidebarWidth,
  getDeviceType,
  getResponsiveSidebarWidth,
  getStoredSidebarWidth,
  setStoredSidebarWidth
} from '../utils/pageLayout';

function Game() {
  const [selectedCategory, setSelectedCategory] = useState('textbook');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [userInput, setUserInput] = useState('');
  const [challenges, setChallenges] = useState([]);
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState('');
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultData, setResultData] = useState({ success: false, message: '' });
  const [currentUser, setCurrentUser] = useState(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(getStoredSidebarWidth);
  const [isResizing, setIsResizing] = useState(false);
  const recognitionRef = useRef(null);
  const isRecordingRef = useRef(false);
  const voiceTranscriptRef = useRef('');
  const scrollContainerRef = useRef(null);  // ✅ 滚动容器引用

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const userInfo = await userAPI.getUserInfo();
          setCurrentUser(userInfo);
        }
      } catch (error) {
        console.error('获取用户信息失败', error);
      }
    };
    fetchCurrentUser();
  }, []);

  // ✅ 滚动监听：滚动后隐藏"更多提示"
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (container.scrollTop > 50) {  // 滚动超过 50px
        setShowScrollHint(false);
      } else {
        setShowScrollHint(true);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [challenges.length]);

  useEffect(() => {
    const handleWindowResize = () => {
      setSidebarWidth(getResponsiveSidebarWidth());
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  useEffect(() => {
    const getChallenges = async () => {
      try {
        const response = await gameAPI.getChallenges({ category: selectedCategory, subCategory: selectedSubCategory });
        setChallenges(response || []);
      } catch (error) {
        console.error('获取挑战列表失败', error);
        setChallenges([]);
      }
    };

    getChallenges();
  }, [selectedCategory, selectedSubCategory]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedSubCategory('');
  };

  const handleSubCategoryChange = (subCategory) => {
    setSelectedSubCategory(subCategory);
  };

  const showResult = (success, message, verificationResult = null) => {
    setResultData({ success, message, verificationResult });
    setShowResultModal(true);
  };
  
  const showVerificationResult = (verificationResult) => {
    if (!verificationResult) return;
    
    const { success, source, message, errors, poemInfo, confidenceScore } = verificationResult;
    
    let modalType = 'default';
    if (success && source === 'library') {
      modalType = 'success-library';
    } else if (!success && source === 'library') {
      modalType = 'failure-library';
    } else if (success && source === 'system_standard') {
      modalType = 'success-system';
    } else if (!success && source === 'system_standard') {
      modalType = 'failure-system';
    } else if (success && (source === 'external_historical' || (source === 'uncertain' && confidenceScore >= 80))) {
      modalType = 'special-achievement'; // 金色特殊表扬
    } else if (!success && source === 'external_historical') {
      modalType = 'failure-external'; // 蓝色温和失败
    } else if (source === 'uncertain') {
      modalType = 'uncertain'; // 黄色存疑
    } else if (source === 'unknown' || !success) {
      modalType = 'encouragement'; // 小清新鼓励
    }
    
    setResultData({ success, message, verificationResult, modalType });
    setShowResultModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // ✅ 校验：如果选择了有子分类的类型，必须选择具体子分类
      const categoriesWithSubCategory = ['flower', 'solar', 'theme', 'poet'];
      if (categoriesWithSubCategory.includes(selectedCategory) && !selectedSubCategory) {
        const categoryNames = {
          'flower': '飞花令',
          'solar': '节气',
          'theme': '题材',
          'poet': '诗人'
        };

        showResult(false, `⚠️ 请选择具体的${categoryNames[selectedCategory]}类型进行挑战！`, null);
        return;
      }

      console.log('🔍 开始智能验证古诗...');
      
      // Step 1: 调用 verify API 进行预验证
      const verifyResponse = await api.post('/api/game/challenges/verify', {
        poem: userInput
      });

      console.log('📦 API 原始响应:', verifyResponse);
      const verificationResult = verifyResponse;  // ✅ 拦截器已返回 response.data
      console.log('✅ 验证结果:', verificationResult);
      
      // Step 2: 根据验证结果显示对应弹窗
      if (!verificationResult.success) {
        // 验证失败 - 显示错误详情弹窗（不保存挑战记录）
        showVerificationResult(verificationResult);
        setUserInput('');
        return;
      }
      
      // Step 3: 验证通过 - 保存挑战记录并增加积分
      const response = await gameAPI.createChallenge({
        poem: userInput,
        category: selectedCategory,
        subCategory: selectedSubCategory,
        userId: currentUser?.id,
        poemId: verificationResult.poemInfo?.id,
        username: currentUser?.phone || '匿名用户',
        nickname: currentUser?.nickname || currentUser?.phone || '我',
        skipVerify: true
      });
      console.log('提交挑战成功', response);

      setChallenges(prev => [
        ...prev,
        {
          id: response.id || Date.now(),
          poemContent: userInput,
          nickname: currentUser?.nickname || currentUser?.phone || '我',
          username: currentUser?.phone || '匿名用户',
          category: selectedCategory,
          subCategory: selectedSubCategory,
          challengeTime: new Date().toISOString()
        }
      ]);

      setUserInput('');

      try {
        await api.put('/api/user/add-score?points=5');
        console.log('积分增加成功：+5分');

        const updatedUserInfo = await userAPI.getUserInfo();
        setCurrentUser(updatedUserInfo);

        window.dispatchEvent(new Event('score-updated'));
      } catch (scoreError) {
        console.error('增加积分失败', scoreError);
      }

      // 显示成功弹窗（包含特殊表扬）
      showVerificationResult(verificationResult);
    } catch (error) {
      console.error('验证/提交挑战失败', error);
      
      if (error.response?.data?.verificationResult) {
        // 如果返回了验证结果，显示验证结果弹窗
        showVerificationResult(error.response.data.verificationResult);
      } else {
        // 其他错误，显示通用错误弹窗
        const errorMsg = error.response?.data?.message || error.message || '验证失败，请重试';
        showResult(false, '❌ ' + errorMsg);
      }
    }
  };

  const stopVoiceRecognition = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
      recognitionRef.current = null;
    }
    isRecordingRef.current = false;
    setIsRecording(false);
  };

  const handleVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showResult(false, '您的浏览器不支持语音识别功能');
      return;
    }

    stopVoiceRecognition();
    voiceTranscriptRef.current = '';

    setShowVoiceModal(true);
    setVoiceStatus('准备开始语音输入...');

    const recognition = new SpeechRecognition();
    recognition.lang = 'zh-CN';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      isRecordingRef.current = true;
      setIsRecording(true);
      setVoiceStatus('🎤 正在聆听，请说出古诗...');
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          voiceTranscriptRef.current += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      const displayText = voiceTranscriptRef.current + interimTranscript;
      if (displayText) {
        setUserInput(displayText);
        setVoiceStatus(
          interimTranscript
            ? `🎤 识别中：${displayText}`
            : `✅ 识别完成：${displayText}`
        );
      }
    };

    recognition.onerror = (event) => {
      if (event.error === 'aborted') {
        return;
      }
      console.error('语音识别错误', event.error);
      const errorMessages = {
        'no-speech': '未检测到语音，请靠近麦克风再试一次',
        'not-allowed': '请允许浏览器使用麦克风权限',
        'network': '语音识别需要网络连接，请检查后重试',
        'audio-capture': '未找到可用麦克风，请检查设备',
      };
      setVoiceStatus(`❌ ${errorMessages[event.error] || `识别失败：${event.error}`}`);
      isRecordingRef.current = false;
      setIsRecording(false);
    };

    recognition.onend = () => {
      isRecordingRef.current = false;
      setIsRecording(false);
      if (voiceTranscriptRef.current) {
        setUserInput(voiceTranscriptRef.current);
        setVoiceStatus(`✅ 识别完成：${voiceTranscriptRef.current}`);
      }
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch (error) {
      console.error('启动语音识别失败', error);
      setVoiceStatus('❌ 无法启动语音识别，请重试');
      isRecordingRef.current = false;
      setIsRecording(false);
    }
  };

  const closeVoiceModal = () => {
    stopVoiceRecognition();
    setShowVoiceModal(false);
  };

  const finishVoiceInput = () => {
    if (voiceTranscriptRef.current) {
      setUserInput(voiceTranscriptRef.current);
    }
    closeVoiceModal();
  };

  const closeResultModal = () => {
    setShowResultModal(false);
  };

  const handleResizeStart = (e) => {
    if (getDeviceType() === 'mobile') return;
    e.preventDefault();
    setIsResizing(true);
    const startX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    const startWidth = typeof sidebarWidth === 'number' ? sidebarWidth : parseInt(sidebarWidth, 10) || getStoredSidebarWidth();

    const handleMove = (moveEvent) => {
      const clientX = moveEvent.type.startsWith('touch') ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const diff = clientX - startX;
      const device = getDeviceType();
      const newWidth = clampSidebarWidth(startWidth + diff, device);
      setSidebarWidth(newWidth);
      if (device !== 'mobile') {
        setStoredSidebarWidth(newWidth);
      }
    };

    const handleUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchmove', handleMove);
    document.addEventListener('touchend', handleUp);
  };

  return (
    <div className="game-container page-container">
      <div className="game-header page-header">
        <h2>古诗挑战</h2>
        <UserInfo currentPage="game" />
      </div>

      <div className="game-content page-content">
        <div
          className="category-sidebar page-sidebar"
          style={{ width: getDeviceType() === 'mobile' ? '100%' : `${sidebarWidth}px`, position: 'relative' }}
        >
          <h3 className="sidebar-title">🎯 挑战类型</h3>
          <div className="category-list">
            <button
              className={`category-item ${selectedCategory === 'textbook' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('textbook')}
            >
              📚 课本单元
            </button>
            <button
              className={`category-item ${selectedCategory === 'flower' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('flower')}
            >
              🌸 飞花令
            </button>
            <button
              className={`category-item ${selectedCategory === 'solar' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('solar')}
            >
              ☀️ 节气
            </button>
            <button
              className={`category-item ${selectedCategory === 'theme' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('theme')}
            >
              📖 题材
            </button>
            <button
              className={`category-item ${selectedCategory === 'poet' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('poet')}
            >
              ✍️ 诗人
            </button>
          </div>

          {selectedCategory === 'flower' && (
            <div className="sub-category">
              <h4>选择关键字</h4>
              <div className="sub-category-list">
                <button
                  className={`sub-category-item ${selectedSubCategory === '春' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('春')}
                >
                  春
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '雨' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('雨')}
                >
                  雨
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '风' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('风')}
                >
                  风
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '花' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('花')}
                >
                  花
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '雪' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('雪')}
                >
                  雪
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '月' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('月')}
                >
                  月
                </button>
              </div>
              {!selectedSubCategory && (
                <p className="sub-category-hint">💡 请先选择一个关键字再进行挑战</p>
              )}
            </div>
          )}

          {selectedCategory === 'solar' && (
            <div className="sub-category">
              <h4>选择节气</h4>
              <div className="sub-category-list">
                <button
                  className={`sub-category-item ${selectedSubCategory === '立春' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('立春')}
                >
                  立春
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '清明' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('清明')}
                >
                  清明
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '冬至' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('冬至')}
                >
                  冬至
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '中秋' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('中秋')}
                >
                  中秋
                </button>
              </div>
              {!selectedSubCategory && (
                <p className="sub-category-hint">💡 请先选择一个节气再进行挑战</p>
              )}
            </div>
          )}

          {selectedCategory === 'theme' && (
            <div className="sub-category">
              <h4>选择题材</h4>
              <div className="sub-category-list">
                <button
                  className={`sub-category-item ${selectedSubCategory === '咏物' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('咏物')}
                >
                  咏物
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '写景' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('写景')}
                >
                  写景
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '送别' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('送别')}
                >
                  送别
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '叙事' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('叙事')}
                >
                  叙事
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '边塞' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('边塞')}
                >
                  边塞
                </button>
              </div>
              {!selectedSubCategory && (
                <p className="sub-category-hint">💡 请先选择一个题材再进行挑战</p>
              )}
            </div>
          )}

          {selectedCategory === 'poet' && (
            <div className="sub-category">
              <h4>选择诗人</h4>
              <div className="sub-category-list">
                <button
                  className={`sub-category-item ${selectedSubCategory === '李白' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('李白')}
                >
                  李白
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '杜甫' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('杜甫')}
                >
                  杜甫
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '王维' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('王维')}
                >
                  王维
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '白居易' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('白居易')}
                >
                  白居易
                </button>
                <button
                  className={`sub-category-item ${selectedSubCategory === '苏轼' ? 'active' : ''}`}
                  onClick={() => handleSubCategoryChange('苏轼')}
                >
                  苏轼
                </button>
              </div>
              {!selectedSubCategory && (
                <p className="sub-category-hint">💡 请先选择一位诗人再进行挑战</p>
              )}
            </div>
          )}
          {getDeviceType() !== 'mobile' && (
            <div className="sidebar-resize-handle" onMouseDown={handleResizeStart} onTouchStart={handleResizeStart} />
          )}
        </div>

        <div className="game-main page-main">
          <div className="challenge-display">
            <h3>{getChallengeTitle(selectedCategory, selectedSubCategory)}</h3>

            <div className="challenge-scroll-container" ref={scrollContainerRef}>
              {challenges.length === 0 ? (
                <div className="empty-challenge">
                  <div className="empty-icon">📚✨</div>
                  <p className="empty-title">这里还没有挑战记录哦~</p>
                  <p className="empty-hint">快来背一首古诗，成为第一个小诗人吧！🌟</p>
                </div>
              ) : (
                <>
                  {/* 显示最新的5条记录 - 带下落动画 */}
                  <div className="challenge-visible-list">
                    {challenges.slice(0, 5).map((challenge, index) => {
                      const categoryStyle = getChallengeStyle(challenge.category, challenge.subCategory);
                      return (
                        <div
                          key={challenge.id}
                          className={`challenge-item ${categoryStyle.className} challenge-fall-in`}
                          style={{ animationDelay: `${index * 0.15}s` }}
                        >
                          <div className={`challenge-decoration ${categoryStyle.decorationClass}`}>
                            {categoryStyle.icon}
                          </div>
                          <div className="challenge-content">
                            <p className="poem-text">
                              {challenge.poemContent && challenge.poemContent.length > 100
                                ? challenge.poemContent.substring(0, 100) + '...'
                                : challenge.poemContent || '无内容'}
                            </p>
                            <div className="challenge-meta">
                              <span className="user-nickname">
                                👤 {challenge.nickname || challenge.username || '匿名用户'}
                              </span>
                              <span className="challenge-time">
                                🕐 {challenge.challengeTime
                                  ? new Date(challenge.challengeTime).toLocaleString('zh-CN', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })
                                  : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* 如果超过5条，显示滚动区域提示（滚动后隐藏） */}
                  {challenges.length > 5 && showScrollHint && (
                    <div className="challenge-more-hint">
                      <span>📜 向下滚动查看更多 ({challenges.length - 5} 条)</span>
                    </div>
                  )}

                  {/* 剩余的记录（默认隐藏，需要滚动） */}
                  <div className="challenge-hidden-list">
                    {challenges.slice(5).map((challenge) => {
                      const categoryStyle = getChallengeStyle(challenge.category, challenge.subCategory);
                      return (
                        <div key={challenge.id} className={`challenge-item ${categoryStyle.className}`}>
                          <div className={`challenge-decoration ${categoryStyle.decorationClass}`}>
                            {categoryStyle.icon}
                          </div>
                          <div className="challenge-content">
                            <p className="poem-text">
                              {challenge.poemContent && challenge.poemContent.length > 100
                                ? challenge.poemContent.substring(0, 100) + '...'
                                : challenge.poemContent || '无内容'}
                            </p>
                            <div className="challenge-meta">
                              <span className="user-nickname">
                                👤 {challenge.nickname || challenge.username || '匿名用户'}
                              </span>
                              <span className="challenge-time">
                                🕐 {challenge.challengeTime
                                  ? new Date(challenge.challengeTime).toLocaleString('zh-CN', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })
                                  : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="challenge-input">
            <form onSubmit={handleSubmit}>
              <div className="input-group">
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder="输入古诗内容..."
                  required
                />
                <button type="button" className="voice-btn" onClick={handleVoiceInput} title="语音输入">
                  🎤
                </button>
              </div>
              <button type="submit" className="btn submit-btn">⚔️ 发起挑战</button>
            </form>
          </div>
        </div>
      </div>

      {showVoiceModal && (
        <div className="modal-overlay" onClick={closeVoiceModal}>
          <div className="lively-modal voice-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">🎤 语音输入古诗</h3>
            <div className="voice-modal-body">
              {isRecording && (
                <div className="recording-animation">
                  <div className="recording-pulse"></div>
                </div>
              )}
              <p className={`voice-status ${isRecording ? 'recording' : ''}`}>
                {voiceStatus}
              </p>
              <div className="voice-modal-actions">
                {isRecording ? (
                  <button type="button" className="confirm-btn" onClick={finishVoiceInput}>
                    完成输入
                  </button>
                ) : !voiceStatus.startsWith('❌') && (voiceStatus.includes('识别完成') || voiceStatus.includes('识别中')) ? (
                  <button type="button" className="confirm-btn" onClick={finishVoiceInput}>
                    确定
                  </button>
                ) : null}
              </div>
            </div>
            <button className="close-modal-btn" onClick={closeVoiceModal} title="关闭">
              ✕
            </button>
          </div>
        </div>
      )}

      {showResultModal && (
        <div className="modal-overlay" onClick={closeResultModal}>
          <div className={`lively-modal result-modal ${getModalClass(resultData)}`} onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">{getModalTitle(resultData)}</h3>
            <div className="result-modal-body">
              <p className={`result-message ${getMessageClass(resultData)}`}>
                {resultData.message}
              </p>
              
              {/* 特殊表扬：显示古诗信息 */}
              {resultData.modalType === 'special-achievement' && resultData.verificationResult?.poemInfo && (
                <div className="poem-info-card">
                  <p className="poem-info-title">📜 识别出的古诗</p>
                  <p className="poem-info-detail">
                    《{resultData.verificationResult.poemInfo.title}》
                    {resultData.verificationResult.poemInfo.author ? 
                      ` - ${resultData.verificationResult.poemInfo.author}` : ''}
                    {resultData.verificationResult.poemInfo.dynasty ? 
                      ` (${resultData.verificationResult.poemInfo.dynasty}代)` : ''}
                  </p>
                  {resultData.verificationResult.confidenceScore > 0 && (
                    <p className="confidence-score">
                      系统置信度：{resultData.verificationResult.confidenceScore}%
                    </p>
                  )}
                </div>
              )}
              
              {/* 错误详情列表 */}
              {resultData.verificationResult?.errors && resultData.verificationResult.errors.length > 0 && (
                <div className="error-details">
                  <p className="error-details-title">📝 错误详情：</p>
                  <ul className="error-list">
                    {resultData.verificationResult.errors.map((error, index) => (
                      <li key={index} className={`error-item ${error.type}`}>
                        <span className="error-type">{getErrorTypeIcon(error.type)}</span>
                        <span className="error-position">{error.position}</span>
                        <span className="error-expected">应为: "{error.expected}"</span>
                        <span className="error-actual">实际: "{error.actual}"</span>
                        {error.suggestion && (
                          <span className="error-suggestion">💡 {error.suggestion}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* 存疑提示 */}
              {resultData.modalType === 'uncertain' && resultData.verificationResult?.confidenceScore > 0 && (
                <div className="uncertain-notice">
                  <p>⚖️ 系统置信度评分: {resultData.verificationResult.confidenceScore}/100</p>
                  <p>💭 这首诗可能是一首标准古诗，但系统无法完全确认</p>
                </div>
              )}
            </div>
            <button className={`confirm-btn ${resultData.success ? 'btn-success' : 'btn-error'}`} onClick={closeResultModal}>
              {resultData.success ? '🎉 太棒了！' : (resultData.modalType === 'failure-library' || resultData.modalType === 'failure-system') ? '🔄 再试一次' : '👌 知道了'}
            </button>
            <button className="close-modal-btn" onClick={closeResultModal} title="关闭">
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function getModalClass(resultData) {
  if (!resultData) return 'error';
  
  const { modalType, success } = resultData;
  
  switch (modalType) {
    case 'success-library':
      return 'success';
    case 'failure-library':
    case 'failure-system':
      return 'error';
    case 'success-system':
      return 'success system-success';
    case 'special-achievement':
      return 'special-achievement'; // 金色主题
    case 'failure-external':
      return 'failure-external'; // 蓝色主题
    case 'uncertain':
      return 'uncertain'; // 黄色主题
    case 'encouragement':
      return 'encouragement'; // 小清新主题
    default:
      return success ? 'success' : 'error';
  }
}

function getModalTitle(resultData) {
  if (!resultData) return '⚠️ 操作提示';
  
  const { modalType, success, verificationResult } = resultData;
  
  switch (modalType) {
    case 'success-library':
      return '🎉 挑战成功！';
    case 'failure-library':
      return '💪 再接再励';
    case 'success-system':
      return '✨ 完全正确！';
    case 'failure-system':
      return '💪 差一点点';
    case 'special-achievement':
      return '🌟 特殊成就解锁！';
    case 'failure-external':
      return '💡 接近了';
    case 'uncertain':
      return '🔍 存疑提示';
    case 'encouragement':
      return '🌱 鼓励';
    default:
      return success ? '🎉 挑战结果' : '⚠️ 操作提示';
  }
}

function getMessageClass(resultData) {
  if (!resultData) return 'error-msg';
  
  const { modalType, success } = resultData;
  
  switch (modalType) {
    case 'success-library':
    case 'success-system':
      return 'success-msg';
    case 'special-achievement':
      return 'special-msg'; // 金色文字
    case 'failure-library':
    case 'failure-system':
      return 'error-msg';
    case 'failure-external':
      return 'gentle-error-msg'; // 温和错误消息
    case 'uncertain':
      return 'uncertain-msg'; // 黄色消息
    case 'encouragement':
      return 'encouragement-msg'; // 小清新消息
    default:
      return success ? 'success-msg' : 'error-msg';
  }
}

function getErrorTypeIcon(type) {
  switch (type) {
    case 'char':
      return '🔤';
    case 'word':
      return '📝';
    case 'sentence':
      return '📄';
    default:
      return '❓';
  }
}

function getChallengeTitle(category, subCategory) {
  switch (category) {
    case 'flower':
      return subCategory ? `🌸 飞花令 - 「${subCategory}」` : '🌸 飞花令';
    case 'solar':
      return subCategory ? `☀️ 节气古诗 - ${subCategory}` : '☀️ 节气古诗';
    case 'theme':
      return subCategory ? `📖 题材 - ${subCategory}` : '📖 题材分类';
    case 'poet':
      return subCategory ? `✍️ 诗人 - ${subCategory}` : '✍️ 诗人作品';
    default:
      return '📚 课本单元挑战';
  }
}

function getChallengeStyle(category, subCategory) {
  const styles = {
    textbook: {
      className: 'textbook-style',
      decorationClass: 'decoration-book',
      icon: '📚'
    },
    flower: {
      春: { className: 'spring-style', decorationClass: 'decoration-leaf', icon: '🌿' },
      雨: { className: 'rain-style', decorationClass: 'decoration-raindrop', icon: '💧' },
      风: { className: 'wind-style', decorationClass: 'decoration-wind', icon: '🍃' },
      花: { className: 'flower-style', decorationClass: 'decoration-flower', icon: '🌸' },
      雪: { className: 'snow-style', decorationClass: 'decoration-snow', icon: '❄️' },
      月: { className: 'moon-style', decorationClass: 'decoration-moon', icon: '🌙' }
    },
    solar: {
      className: 'solar-style',
      decorationClass: 'decoration-sun',
      icon: '☀️'
    },
    theme: {
      咏物: { className: 'object-style', decorationClass: 'decoration-scroll', icon: '📜' },
      写景: { className: 'scenery-style', decorationClass: 'decoration-mountain', icon: '⛰️' },
      送别: { className: 'farewell-style', decorationClass: 'decoration-path', icon: '🛤️' },
      叙事: { className: 'story-style', decorationClass: 'decoration-book-open', icon: '📖' },
      边塞: { className: 'frontier-style', decorationClass: 'decoration-horse', icon: '🐎' }
    },
    poet: {
      className: 'poet-style',
      decorationClass: 'decoration-brush',
      icon: '✒️'
    }
  };

  if (category === 'flower' && subCategory && styles.flower[subCategory]) {
    return styles.flower[subCategory];
  }

  if (category === 'theme' && subCategory && styles.theme[subCategory]) {
    return styles.theme[subCategory];
  }

  return styles[category] || styles.textbook;
}

export default Game;